import i18next from 'i18next';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';

import { buildPatientInfo } from '../utils/patient-info';
import { renderPatientCell } from '../patient-cell';
import DueDateCell from '../due-date-cell';
import NoteCell from '../note-cell';
import s from './styles.module.scss';
import NotePreview from '../note-preview';

import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IKeyNoteData,
  IPartialsTableData,
  IPartialsTableDataFilters,
} from 'infrastructure/interfaces';

interface Props {
  filter: IPartialsTableDataFilters;
}

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<IPartialsTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  return [
    {
      label: i18next.t('labels.patient'),
      key: 'name',
      width: '25%',
      align: 'left',
      dataCy: 'partials-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['p-name'] ? tableSortMap[filter['p-name']] : undefined,
      render(_name: string, record) {
        return renderPatientCell({ record });
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '15%',
      align: 'left',
      dataCy: 'partials-patients-provider',
      sortable: true,
      sortOrder: filter['p-provider']
        ? tableSortMap[filter['p-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.callsMonth'),
      key: 'callsMonth',
      align: 'left',
      dataCy: 'partials-patients-calls-month',
      sortable: true,
      sortOrder: filter['p-callsMonth']
        ? tableSortMap[filter['p-callsMonth']]
        : undefined,
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      align: 'left',
      dataCy: 'partials-patients-due-date',
      sortable: true,
      sortOrder: filter['p-dueDate']
        ? tableSortMap[filter['p-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      align: 'left',
      dataCy: 'partials-patients-appointment-date',
      sortable: true,
      sortOrder: filter['p-appointmentDate']
        ? tableSortMap[filter['p-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return DateFormatter({
          date: appointmentDate,
          format: DateFormats['MM/dd/yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.minutes'),
      key: 'minutes',
      align: 'left',
      dataCy: 'partials-patients-minutes',
      sortable: true,
      sortOrder: filter['p-minutes']
        ? tableSortMap[filter['p-minutes']]
        : undefined,
    },
    {
      label: i18next.t('labels.nextBillable'),
      key: 'nextBillable',
      align: 'left',
      dataCy: 'partials-patients-next-billable',
      sortable: true,
      sortOrder: filter['p-nextBillable']
        ? tableSortMap[filter['p-nextBillable']]
        : undefined,
    },
    {
      label: i18next.t('labels.lastCall'),
      key: 'lastCallDate',
      align: 'left',
      dataCy: 'partials-patients-last-call',
      sortable: false,
      sortOrder: filter['p-lastCallDate']
        ? tableSortMap[filter['p-lastCallDate']]
        : undefined,
      render(lastCallDate: string) {
        return DateFormatter({
          date: lastCallDate,
          format: DateFormats['MM/dd/yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.notes'),
      key: 'notes',
      width: '10%',
      align: 'left',
      render(notes: IKeyNoteData[] = []) {
        return <NotePreview notes={notes} />;
      },
    },
    {
      label: '',
      key: 'noteaction',
      width: '3%',
      align: 'left',
      render(_unused: IKeyNoteData[] = [], record) {
        const noteList = record.notes ?? [];
        return (
          <NoteCell
            length={noteList.length}
            onClick={() => {
              setTableNote({
                notes: noteList,
                tableName: 'partials',
                patientGuid: record.guid,
                patientName: record.name,
                patientInfo: buildPatientInfo(record),
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'partials-patients-notes',
    },
  ];
};
